import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Clock, Volume2, VolumeX, MapPin, CalendarPlus } from "lucide-react";
import { Reveal, Rise } from "./Reveal";
import WhatsAppShareButton from "./WhatsAppShareButton";
import content from "../content";

const CROSSFADE_MS = 4000; // 4 segundos por imagen
const CTA_ICONS = { MapPin, CalendarPlus };

function GatewayActions({ gateway }) {
  const invite = gateway.invite;
  const getInvite = useCallback(async () => {
    const blob = await (await fetch(invite.image)).blob();
    return new File([blob], invite.fileName, { type: blob.type || "image/jpeg" });
  }, [invite]);

  return (
    <>
      <div className="mt-9 flex flex-wrap justify-center lg:justify-start gap-3">
        {gateway.ctas?.map((c, i) => {
          const Icon = CTA_ICONS[c.icon] || MapPin;
          return (
            <a
              key={c.label}
              href={c.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`gateway-cta-${i}`}
              className={`inline-flex items-center gap-2.5 rounded-full px-6 py-3.5 font-poppins font-semibold text-sm uppercase tracking-[0.12em] transition-colors duration-300 ${
                c.variant === "primary"
                  ? "bg-electric text-white hover:bg-white hover:text-ink shadow-[0_0_28px_rgba(33,112,232,0.45)]"
                  : "border border-white/20 bg-white/5 text-white hover:bg-white/15"
              }`}
            >
              <Icon size={17} /> {c.label}
            </a>
          );
        })}
      </div>
      {invite && !invite.hidden && (
        <WhatsAppShareButton
          getFile={getInvite}
          text={invite.text}
          labels={invite}
          testId="gateway-invite-btn"
          className="mt-4 lg:items-start"
        />
      )}
    </>
  );
}

export default function GatewaySection() {
  const { gateway } = content;
  const reduced = useReducedMotion();

  // Video: arranca en silencio, toca para oír el audio
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const toggleAudio = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) v.play().catch(() => {});
    setMuted(v.muted);
  };

  // Crossfade entre la foto y la imagen de invitación
  const slides = [gateway.image, gateway.inviteImage].filter(Boolean);
  const slidesAlt = [gateway.imageAlt, gateway.inviteImageAlt];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setIdx((p) => (p + 1) % slides.length), CROSSFADE_MS);
    return () => clearInterval(t);
  }, [slides.length]);

  return (
    <section id="gateway" className="relative w-full bg-navy py-28 md:py-36 overflow-hidden" data-testid="gateway-section">
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{ background: "radial-gradient(circle at 25% 40%, rgba(33,112,232,0.2), transparent 60%)" }}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Media: video 9:16 + poster que se funde, lado a lado */}
        <Reveal className="relative order-1" amount={0.2}>
          <Rise className="relative flex flex-col sm:flex-row justify-center lg:justify-start items-start gap-5">
            {/* Reproductor de video vertical */}
            <div className="relative w-full sm:w-1/2 max-w-[300px] mx-auto sm:mx-0">
              <div className="absolute -inset-4 rounded-[2.5rem] bg-electric/20 blur-3xl" aria-hidden="true" />
              <button
                type="button"
                onClick={toggleAudio}
                aria-label={muted ? "Activar audio del video" : "Silenciar video"}
                data-testid="gateway-video-audio-toggle"
                className="group relative block w-full aspect-[9/16] overflow-hidden rounded-[1.75rem] border border-white/10 shadow-[0_30px_80px_-25px_rgba(0,0,0,0.9)] cursor-pointer"
              >
                <video
                  ref={videoRef}
                  className="absolute inset-0 w-full h-full object-cover"
                  autoPlay={!reduced}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  poster={gateway.image}
                  data-testid="gateway-video"
                >
                  <source src={gateway.video} type="video/mp4" />
                </video>
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
                <span className="absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 rounded-full bg-black/55 backdrop-blur-sm px-3.5 py-1.5 font-poppins text-[11px] tracking-wide text-white transition-transform duration-300 group-hover:scale-105">
                  {muted ? <VolumeX size={14} /> : <Volume2 size={14} className="text-electric" />}
                  {muted ? gateway.videoAudioHint : "Sonido activado"}
                </span>
                <span className="pointer-events-none absolute inset-0 rounded-[1.75rem] ring-1 ring-inset ring-white/10" />
              </button>
            </div>

            {/* Poster con crossfade */}
            <div className="relative w-full sm:w-1/2 max-w-[300px] mx-auto sm:mx-0">
              <div className="absolute -inset-4 rounded-[2.5rem] bg-electric/15 blur-3xl" aria-hidden="true" />
              <div
                className="relative w-full aspect-[4/5] overflow-hidden rounded-[1.75rem] border border-white/10 shadow-[0_30px_80px_-25px_rgba(0,0,0,0.9)]"
                data-testid="gateway-crossfade"
              >
                <AnimatePresence>
                  <motion.img
                    key={slides[idx]}
                    src={slides[idx]}
                    alt={slidesAlt[idx]}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: reduced ? 0 : 1.2, ease: "easeInOut" }}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </AnimatePresence>
                <span className="pointer-events-none absolute inset-0 rounded-[1.75rem] ring-1 ring-inset ring-white/10" />
              </div>
            </div>
          </Rise>
        </Reveal>

        {/* Text */}
        <Reveal className="order-2 text-center lg:text-left">
          <Rise>
            <p className="eyebrow mb-5">{gateway.eyebrow}</p>
          </Rise>
          <Rise>
            <h2 className="display text-white text-[clamp(2.5rem,7vw,5.5rem)] mb-6">
              {gateway.title}
            </h2>
          </Rise>
          <Rise>
            <div className="inline-flex items-center gap-2.5 rounded-full border border-electric/50 bg-electric/10 px-5 py-2.5 mb-7">
              <Clock size={16} className="text-electric" />
              <span className="font-poppins text-white text-sm font-semibold tracking-wide uppercase">
                {gateway.time}
              </span>
            </div>
          </Rise>
          <Rise>
            <p className="font-poppins text-ceramic/80 text-base md:text-lg leading-relaxed max-w-xl mx-auto lg:mx-0">
              {gateway.text}
            </p>
          </Rise>
          <Rise>
            <GatewayActions gateway={gateway} />
          </Rise>
        </Reveal>
      </div>
    </section>
  );
}
